import type { StepDisplay } from "../utils/toolCallProgress";

import React from "react";

import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import twMerge from "@replo/design-system/utils/twMerge";
import { AnimatePresence, motion } from "framer-motion";
import { Circle } from "lucide-react";

import { shimmerStyles } from "../utils/shimmerStyles";

const FilledCheckIcon = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8ZM12.0303 4.96967C11.7374 4.67678 11.2626 4.67678 10.9697 4.96967C10.9626 4.97674 10.9559 4.98424 10.9498 4.9921L7.4774 9.41674L5.38388 7.32322C5.09098 7.03033 4.61611 7.03033 4.32322 7.32322C4.03032 7.61612 4.03032 8.09099 4.32322 8.38388L6.96966 11.0303C7.26256 11.3232 7.73743 11.3232 8.03032 11.0303C8.03685 11.0238 8.043 11.0169 8.04876 11.0097L12.041 6.01947C12.3232 5.72582 12.3196 5.25897 12.0303 4.96967Z"
        fill="black"
      />
    </svg>
  );
};

interface ProgressCardProps {
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
  progress?: StepDisplay[];
  showCondensedProgress?: boolean;
  title?: string;
  subtitle?: string;
  className?: string;
}

const ProgressCard = ({
  startEnhancer,
  endEnhancer,
  progress,
  showCondensedProgress = true,
  title = "Updating page...",
  subtitle,
  className,
}: ProgressCardProps) => {
  const firstIncompleteStep = progress?.find((step) => !step.isCompleted);

  return (
    // Note (Noah, 2025-08-13): You need two divs to properly render a linear gradient
    // border on a div with a different linear gradient background, see
    // https://codyhouse.co/nuggets/css-gradient-borders
    <div
      className={twMerge("w-[400px] max-w-full p-0", className)}
      style={{
        background:
          "linear-gradient(white, white) padding-box, linear-gradient(var(--replo-gradient-border-angle), rgba(253, 211, 126) 0%, rgba(243, 77, 114) 50%, rgba(124, 127, 186) 100%) border-box",
        border: "2px solid transparent",
        borderRadius: "12px",
        animation: "8s replo-gradient-border-rotate linear infinite",
      }}
    >
      <div
        className="flex flex-col"
        style={{
          backgroundImage:
            "linear-gradient(90deg, rgba(253, 211, 126, 0.05) 0%, rgba(243, 77, 114, 0.05) 50%, rgba(124, 127, 186, 0.05) 100%)",
        }}
      >
        {/* Top section with title and enhancers */}
        <div
          className={twMerge(
            "flex flex-row gap-3 mx-3 py-3",
            progress &&
              !showCondensedProgress &&
              "border-b-[0.5px] border-border",
          )}
        >
          {startEnhancer}
          <div className="flex flex-col items-start gap-1 w-full h-full">
            <div
              className="header-h4 w-full line-clamp-1 overflow-ellipsis"
              style={progress ? shimmerStyles : {}}
            >
              {showCondensedProgress && progress
                ? progress[progress.length - 1]?.headline ?? title
                : title}
            </div>
            {subtitle && !showCondensedProgress && (
              <p className="text-sm text-muted">{subtitle}</p>
            )}
            {showCondensedProgress &&
              progress &&
              progress[progress.length - 1]?.subheadline && (
                <p className="text-sm text-muted">
                  {progress[progress.length - 1]?.subheadline ?? subtitle}
                </p>
              )}
          </div>
          {endEnhancer}
        </div>

        {/* Progress steps section */}
        {progress && !showCondensedProgress && (
          <div className="flex flex-col gap-1 p-3">
            <AnimatePresence initial={false}>
              {progress.map((step, index) => (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, y: 10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="flex flex-row items-stretch gap-2 relative"
                >
                  <div className="flex flex-col items-center flex-shrink-0 relative min-w-[24px]">
                    {firstIncompleteStep?.id === step.id && (
                      <Spinner variant="ai" size={16} />
                    )}

                    {firstIncompleteStep?.id !== step.id &&
                      step.isCompleted && <FilledCheckIcon />}

                    {firstIncompleteStep?.id !== step.id &&
                      !step.isCompleted && <Circle size={16} />}

                    {index < progress.length - 1 && (
                      <div className="w-[1px] bg-default min-h-2 h-full mt-1 flex-1" />
                    )}
                  </div>

                  <div className="flex flex-col flex-1 min-w-0 gap-1 h-full justify-center pb-1">
                    <div className="text-sm font-medium line-clamp-2 overflow-ellipsis">
                      {step.headline}
                    </div>
                    {step.subheadline && !step.isCompleted && (
                      <div className="text-sm text-muted">
                        {step.subheadline}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressCard;
